import type { BlogPost } from "./posts";

export const advancedPosts: BlogPost[] = [
  {
    slug: "advanced-canvas-drawing-techniques",
    title: "Advanced Canvas Drawing Techniques in OmniCanvas",
    description:
      "Go beyond the basics: layering, opacity, combining shapes, complex diagrams, Smart Shapes for clean output, and mixing freehand with geometric shapes.",
    date: "2027-04-04",
    readTime: "7 min read",
    category: "Tutorials",
    content: `
## Why Drawing Technique Matters on an Infinite Canvas

Most people start with OmniCanvas by dropping sticky notes and typing text. That alone is powerful, but the real magic happens when you use the full Excalidraw-based drawing toolkit to create visual explanations, diagrams, and illustrations right alongside your notes. Once you move beyond basic shapes, your canvas becomes a true thinking surface where ideas take visual form.

This guide covers six advanced techniques that will transform the way you draw in OmniCanvas. If you are new to the canvas, the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas) covers the basics first.

## Layering Elements for Depth and Clarity

One of the most underused capabilities in OmniCanvas is deliberate layering. Every element you place on the canvas has a stacking order. You can bring elements forward or send them backward to create layered compositions.

**Practical layering strategies:**

- Draw a large, lightly filled rectangle as a background zone, then send it to the back. Place sticky notes and shapes on top of it to create a visually grouped region.
- Use semi-transparent shapes as overlays to highlight areas of a complex diagram without hiding the content beneath.
- Stack multiple shapes to build composite icons or symbols that you cannot create with a single tool.

The key insight is that your canvas is not flat. Think of it as a stack of transparent sheets. By controlling which layer each element sits on, you create drawings that communicate hierarchy and grouping at a glance.

## Using Opacity to Communicate Priority

Not every element on your canvas deserves the same visual weight. Opacity is your tool for creating a visual hierarchy without deleting or hiding anything.

**Try these approaches:**

- Set background zones to 20-30 percent opacity so they tint the area without competing with foreground content.
- Use full opacity for current, active elements and reduce opacity on archived or lower-priority items.
- Create "ghost" versions of shapes at low opacity to indicate planned but not yet confirmed elements in a project diagram.

This technique is especially powerful in project planning canvases where you want to see the full picture but need active tasks to stand out visually.

## Combining Shapes to Build Complex Diagrams

Individual rectangles, circles, and arrows are building blocks. The real skill is combining them into meaningful diagrams. OmniCanvas gives you the full Excalidraw shape library, which means you can build anything from flowcharts to system architecture diagrams.

### Flowcharts and Decision Trees

1. Start with a rectangle for each process step.
2. Use diamonds for decision points.
3. Connect them with arrows, and label each arrow with the condition or outcome.
4. Group related steps by placing them on a shared background rectangle.

### System Architecture Diagrams

1. Use rectangles for services or components.
2. Use rounded rectangles for databases or storage layers.
3. Draw arrows to show data flow, and label each with the protocol or data type.
4. Add sticky notes next to complex components to capture explanations or open questions.

### Mind Maps

1. Place your central topic as a large shape in the middle of the canvas.
2. Draw lines outward to subtopic shapes.
3. Use color to group related branches.
4. Add freehand annotations or small sketches at the leaf nodes.

The infinite canvas means you never run out of room. Let your diagrams grow naturally and reorganize them as your understanding evolves.

## Smart Shapes: From Rough Sketch to Clean Output

OmniCanvas includes Smart Shapes, an AI-powered feature that recognizes your freehand drawings and converts them into clean geometric shapes (see our full [Smart Shapes guide](/blog/tutorials/omnicanvas-smart-shapes-guide)). This is a game-changer for people who think by sketching but want polished output.

**How to get the best results from Smart Shapes:**

- Draw deliberately. A single confident stroke for each line or curve works better than sketchy, overlapping strokes.
- Pause briefly after completing a shape so the recognition engine can process it.
- Draw closed shapes when you want filled rectangles, circles, or triangles. Leave shapes open when you want lines or arrows.
- If Smart Shapes misinterprets your intent, undo and try again with a slightly more exaggerated version of the shape. For example, make corners sharper for rectangles or curves rounder for circles.

Smart Shapes is particularly useful for creating professional-looking diagrams during meetings or brainstorming sessions. You sketch naturally, and the tool cleans up after you.

## Mixing Freehand and Geometric Elements

Some of the most effective canvases combine the warmth of hand-drawn elements with the precision of geometric shapes. This is not just an aesthetic choice. It communicates something meaningful: geometric shapes feel structured and decided, while freehand elements feel exploratory and in-progress.

**Practical combinations:**

- Use geometric rectangles and arrows for the confirmed parts of a workflow, and freehand circles and squiggly lines for the parts you are still figuring out.
- Annotate clean diagrams with freehand arrows and handwritten notes to add context without making the diagram itself more complex.
- Create hand-drawn borders or dividers to separate canvas zones in a way that feels organic rather than rigid.

This mixed approach works because it mirrors how real thinking works. Some ideas are firm and structured. Others are fuzzy and evolving. Your canvas should reflect that.

## Putting It All Together: A Complex Canvas Example

Imagine you are planning a product launch. Here is how you might combine all of these techniques on a single canvas:

1. Create three large, low-opacity background rectangles as zones: Pre-Launch, Launch Day, and Post-Launch.
2. Within each zone, draw a flowchart using geometric shapes for confirmed tasks and freehand shapes for tentative ones.
3. Layer sticky notes on top of the flowchart to capture detailed notes about specific tasks.
4. Use Smart Shapes to quickly sketch status icons next to each task.
5. Connect related tasks across zones with arrows to show dependencies.

The result is a single canvas that serves as both a high-level plan and a detailed reference. You can zoom out to see the full picture or zoom in to work on any specific area. That is the power of advanced drawing in OmniCanvas.
`,
  },
  {
    slug: "build-daily-dashboard-canvas",
    title: "Building a Daily Dashboard Canvas in OmniCanvas",
    description:
      "Create a reusable daily dashboard canvas with zones for today's priorities, ongoing projects, quick capture and reference info. Includes layouts.",
    date: "2027-04-11",
    readTime: "7 min read",
    category: "Tutorials",
    content: `
## Why a Daily Dashboard Canvas Works

A daily dashboard is a single canvas that gives you a bird's-eye view of your day. Instead of checking multiple apps for tasks, notes, and reference material, you open one canvas and everything is there: priorities, projects, quick capture area, and reference links. The spatial layout means your brain can orient itself instantly. Top-left is priorities, bottom-right is reference material, and you always know where to look.

This tutorial walks you through building a daily dashboard canvas in OmniCanvas that you can reuse every day. New to OmniCanvas? Start with the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas).

## Step 1: Define Your Zones

The foundation of a good dashboard is clear zones. Each zone is a region of the canvas dedicated to a specific purpose. Here is a layout that works well for most people:

**Top-Left: Today's Priorities (the largest zone)**

This is where your eyes go first. Place three to five sticky notes here, one for each priority. Use a bold color like red or orange for must-do items and a calmer color like blue or green for nice-to-have tasks.

**Top-Right: Ongoing Projects**

List your active projects here. Each project gets a sticky note with its name and current status. This zone does not change daily. You update it when projects start or finish, which might be weekly.

**Bottom-Left: Quick Capture**

This is your inbox. Throughout the day, drop new ideas, requests, and random thoughts here as sticky notes. Do not organize them yet. At the end of the day, review this zone and move items to the appropriate place.

**Bottom-Right: Reference Info**

Keep frequently needed information here: links, account numbers, team contact info, recurring meeting agendas. This zone rarely changes.

## Step 2: Create the Visual Structure

Open a new canvas in OmniCanvas and start building the structure:

1. Draw four large rectangles to define your zones. Use the rectangle tool and make each one large enough to hold five to eight sticky notes comfortably.
2. Set each rectangle to a different fill color with low opacity so they tint the background without hiding content. For example, light red for Priorities, light blue for Projects, light yellow for Quick Capture, and light gray for Reference.
3. Add a text label at the top of each rectangle with the zone name in bold.
4. Leave generous spacing between zones so the boundaries are clear even when you are zoomed out.

### Sizing Tips

- Your Priorities zone should be about 40 percent of the canvas area. This is the zone you interact with most.
- Quick Capture should be easy to reach, so place it in the lower-left where a quick scroll or zoom gets you there.
- Reference can be the smallest zone since you read it but rarely edit it.

## Step 3: Populate with Starter Content

**Priorities zone:** Add three sticky notes with today's top tasks. Use red sticky notes for urgent items and green for important-but-not-urgent ones. Write a clear, action-oriented title on each note. If a task needs more detail, add it as body text within the note.

**Projects zone:** Add one sticky note per active project. Include the project name, your role, and the next action you owe. Use blue sticky notes for work projects and purple for personal ones.

**Quick Capture zone:** Leave this empty to start. It fills up naturally throughout the day.

**Reference zone:** Add sticky notes for information you look up repeatedly. Phone numbers, server addresses, standard operating procedures, or project briefs all work well here.

## Step 4: Establish a Daily Routine

A dashboard canvas only works if you use it consistently. Here is a simple daily routine:

**Morning (5 minutes):**

1. Open your dashboard canvas.
2. Clear completed priorities from yesterday. Move them off-canvas or delete them.
3. Add today's priorities as new sticky notes in the Priorities zone.
4. Glance at Ongoing Projects and update any statuses that changed.

**Throughout the Day:**

- When a new idea or request comes in, drop a sticky note in the Quick Capture zone. Do not stop to organize it.
- When you complete a priority, change the sticky note color to a muted tone or add a checkmark to the title.

**End of Day (5 minutes):**

1. Review the Quick Capture zone. Move actionable items to Priorities (for tomorrow) or to a project-specific canvas.
2. Delete or archive anything that is no longer relevant.
3. Update the Projects zone if any status changed.

## Step 5: Enhance with Drawing Elements

Once the basic dashboard is working, add drawing elements to make it more functional:

- **Arrows** between a Priority sticky note and its related Project to show connections.
- **Divider lines** within the Priorities zone to separate must-do from nice-to-have.
- **Freehand circles** around items that need attention or follow-up.
- **Small icons** drawn with the pen tool to visually categorize items (a phone for calls, an envelope for emails, a calendar for meetings).

## Tips for Long-Term Success

**[Pin your dashboard note](/blog/tutorials/pinning-notes-quick-access)** so it always appears at the top of your notes list. This makes it one click away no matter which view you are using.

**Use tags** to mark your dashboard note with a "dashboard" tag. If you create specialized dashboards later (a weekly review dashboard, a project-specific dashboard), you can find them all instantly with a tag search.

**Do not over-engineer it.** The dashboard should take less than five minutes to set up each morning. If it takes longer, simplify. Remove zones you do not use. Reduce the number of sticky notes. The best dashboard is the one you actually open every day.

**Duplicate for a fresh start.** If your dashboard gets cluttered after a few weeks, create a new canvas from scratch. Move over only the items that are still relevant. This regular reset keeps your dashboard clean and focused.
`,
  },
  {
    slug: "multi-folder-notes-cross-referencing",
    title: "Cross-Referencing Notes with Multiple Folders in OmniCanvas",
    description:
      "How to use OmniCanvas's multi-folder feature to file a single note in multiple folders, enabling cross-referencing without duplication.",
    date: "2027-04-18",
    readTime: "6 min read",
    category: "Tutorials",
    content: `
## The Problem with Single-Folder Systems

In most notetaking apps, each note lives in exactly one folder. This forces you to make an impossible choice. Does your meeting notes document about the Q3 marketing budget belong in the "Meetings" folder or the "Marketing" folder or the "Budgets" folder? You pick one location, and then you cannot find the note when you are thinking about it from a different angle.

Some people solve this by duplicating notes across folders. But duplicates create a nightmare: you update one copy and forget the other, leading to conflicting information scattered across your system.

OmniCanvas solves this with multi-folder notes. A single note can belong to multiple folders simultaneously. No duplication, no conflicts, no impossible choices. If you are just getting started, the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas) covers the basics of notes and folders.

## How Multi-Folder Notes Work in OmniCanvas

When you create or edit a note in OmniCanvas, you can assign it to as many folders as you want. The note itself is stored once. Each folder simply holds a reference to it. When you update the note, the changes appear everywhere it is filed.

This means you can browse your "Meetings" folder and see the Q3 marketing budget note right there. Then switch to "Marketing" and see the same note. Open it from either location and you are editing the same document.

## Setting Up a Cross-Referenced Folder Structure

To get the most out of multi-folder notes, you need a folder structure designed for cross-referencing. Here is a structure that works well:

### By Area of Responsibility

Create top-level folders for each area of your life or work:

- Work
- Personal
- Health
- Finance
- Learning

### By Project

Create folders for active projects, nested under their area:

- Work / Website Redesign
- Work / Q3 Campaign
- Personal / Home Renovation

### By Type

Create folders for note types that cross all areas:

- Meeting Notes
- Reference
- Ideas
- Templates

### The Cross-Referencing Magic

Now, when you take notes during a meeting about the website redesign, file that note in both "Meeting Notes" and "Work / Website Redesign." When you capture an idea for the Q3 campaign while reading an article, file it in both "Ideas" and "Work / Q3 Campaign."

Every note appears in every relevant context. You never have to remember which folder you chose.

## Practical Cross-Referencing Strategies

### Strategy 1: The Project-Plus-Type Method

Every note gets filed in at least two folders: one project folder and one type folder. A brainstorming session for the website redesign goes in "Work / Website Redesign" and "Ideas." A budget spreadsheet goes in "Work / Q3 Campaign" and "Reference."

This means you can browse by project when you are working on something specific, or browse by type when you want to see all your meeting notes or all your reference materials.

### Strategy 2: The Stakeholder Method

If you work with multiple teams or clients, create folders for each stakeholder. Then file notes in both the project folder and the stakeholder folder. When a client asks what you have been working on for them, open their folder and every relevant note is there.

### Strategy 3: The Timeline Method

Create folders for time periods: "2027 Q1," "2027 Q2," and so on. File every note in both its topic folder and the relevant time-period folder. This gives you a chronological view alongside your topical view.

## Combining Multi-Folder Notes with Tags

Multi-folder filing handles broad categorization. Tags handle fine-grained attributes (our [folders and tags guide](/blog/tutorials/omnicanvas-folders-tags-organization) covers both in depth). Use them together for a powerful system:

- **Folders** for where a note belongs (project, area, type).
- **Tags** for what a note is about (status, priority, topic keywords).

For example, a note filed in "Work / Website Redesign" and "Meeting Notes" might also have tags like "design," "stakeholder-feedback," and "action-items." You can then use OmniCanvas search with Cmd+K to find all notes tagged "action-items" across every project.

## Discovering Connections with the Knowledge Graph

OmniCanvas has a knowledge graph view that visualizes connections between your notes based on folder membership, shared tags, and content similarity. When you file notes in multiple folders, the knowledge graph becomes dramatically more useful.

Notes that share multiple folders show up as strongly connected in the graph. This makes it easy to spot clusters of related information and discover connections you might not have noticed otherwise. Adjust the similarity threshold to control how aggressively the graph links notes by content, and watch unexpected relationships emerge.

## Avoiding Common Mistakes

**Do not create too many folders.** Multi-folder notes reduce the need for deeply nested folder hierarchies. If you find yourself creating a folder that will only hold two or three notes, consider using tags instead.

**Be consistent with your filing rules.** Decide on a system (like the project-plus-type method) and stick with it. Inconsistent filing undermines the whole approach.

**Review your folder structure quarterly.** Archive folders for completed projects. Merge folders that overlap too much. A clean folder structure makes multi-folder filing effortless.

**Use search as a safety net.** Even with great organization, sometimes you will forget where you filed something. The Cmd+K search in OmniCanvas searches across titles, content, tags, and folders, so you can always find what you need regardless of how it is organized.
`,
  },
  {
    slug: "omnicanvas-dark-mode-tips",
    title: "Dark Mode in OmniCanvas: Setup, Benefits, and Best Practices",
    description:
      "How to enable and get the most out of dark mode, including benefits for eye strain, night work, and focus.",
    date: "2027-04-25",
    readTime: "5 min read",
    category: "Tutorials",
    content: `
## Why Dark Mode Matters for Notetaking

Dark mode is not just an aesthetic preference. For people who spend hours working with notes, canvases, and diagrams, it is a functional choice that affects comfort, focus, and even creativity. OmniCanvas includes a built-in dark mode toggle that transforms the entire interface, and understanding how to use it well can meaningfully improve your experience. New to OmniCanvas? Start with the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas).

## How to Enable Dark Mode

Turning on dark mode in OmniCanvas takes one click. Look for the dark mode toggle in the app settings. Click it and the entire interface switches from a light background to a dark one. Click again to switch back. Your preference is saved automatically and persists across sessions.

The toggle affects everything: the sidebar, note list, canvas background, sticky notes, toolbar, and all interface elements. The transition is instant with no need to reload or restart.

## Benefits of Working in Dark Mode

### Reduced Eye Strain During Long Sessions

When you are building complex canvases, organizing dozens of notes, or working through a detailed knowledge graph, you might spend hours looking at the screen. A bright white background in a dim room forces your pupils to contract, which leads to fatigue over time. Dark mode reduces the overall brightness of the interface, easing the strain on your eyes.

This is especially noticeable during evening and nighttime work sessions. If you tend to do your best thinking after dinner, dark mode makes those sessions significantly more comfortable.

### Better Focus on Content

A dark background naturally draws your attention to the content that is displayed on it. Sticky notes, drawn shapes, and text all pop against a dark canvas. This figure-ground contrast helps you focus on what matters: your ideas, not the interface surrounding them.

Many users report that they find it easier to enter a flow state in dark mode because the interface recedes and the content comes forward.

### Reduced Blue Light Exposure

While dark mode is not a complete blue light filter, it does reduce the total amount of light your screen emits. For people who are sensitive to blue light or who want to minimize screen brightness before bed, dark mode is a meaningful step in the right direction.

## Best Practices for Dark Mode

### Choose Sticky Note Colors Deliberately

OmniCanvas offers six sticky note colors. In light mode, all six are easy to distinguish. In dark mode, some colors look different than you might expect. Spend a few minutes creating test sticky notes in each color to see how they look against the dark canvas.

Bright colors like yellow and orange tend to stand out dramatically in dark mode, making them ideal for high-priority items. Cooler colors like blue and green blend more subtly into the dark background, which makes them good for reference notes or lower-priority items.

### Adjust Your Drawing Style

When drawing on a dark canvas, light-colored strokes are more visible than dark ones. If you have been using black or dark gray as your default drawing color, switch to white or a light gray in dark mode. For more on getting clean results, see [advanced canvas drawing techniques](/blog/tutorials/advanced-canvas-drawing-techniques). This ensures your diagrams and annotations remain clearly visible.

Bold, thick strokes also work better on dark backgrounds than thin, delicate ones. If you use fine lines for detailed diagrams, consider increasing the stroke width slightly when working in dark mode.

### Consider Your Export Needs

When you export a canvas to PDF, OmniCanvas gives you the option to choose between a white background and a transparent background. If you have been working in dark mode and your canvas looks great with dark surroundings, be aware that exporting with a white background will change how the canvas looks. Elements that were bright and vivid against a dark background may look washed out on white.

If your exported PDFs are for your own reference, transparent backgrounds preserve the look you intended. If the PDFs are for sharing with others who will print them, a white background with appropriately colored elements works better.

### Use Dark Mode for Different Work Phases

Some users find it helpful to associate dark mode with specific types of work. For example:

- **Dark mode for creative work.** Brainstorming, mind mapping, and freeform canvas exploration feel more immersive in dark mode.
- **Light mode for review and editing.** When you are polishing notes, checking details, or preparing content for sharing, light mode can feel more precise and businesslike.
- **Dark mode for evening, light mode for daytime.** Match your interface to the ambient lighting in your workspace.

There is no right answer here. The point is that having the toggle available lets you adapt your environment to your current task and energy level.

## Dark Mode and the Knowledge Graph

The knowledge graph view is particularly striking in dark mode. Nodes and connection lines appear as bright elements against a dark background, which makes the structure of your knowledge network visually dramatic and easy to read. If you have not explored the knowledge graph yet, try it in dark mode first. The visual clarity may surprise you.

## Making the Switch

If you have been using light mode exclusively, try committing to dark mode for one full week. It takes a day or two for your eyes and brain to adjust to the new color scheme. After that adjustment period, most users find that they strongly prefer one mode over the other. And since switching is instant, you can always go back.

The most important thing is that your notetaking environment works for you. Dark mode is a tool for comfort and focus. Use it when it helps, and do not feel obligated to use it when it does not.
`,
  },
  {
    slug: "pinning-notes-quick-access",
    title: "How to Pin Notes for Quick Access in OmniCanvas",
    description:
      "Use pinning to keep your most important notes at the top of every view. What to pin, when to unpin, and how pins combine with other organization.",
    date: "2027-05-02",
    readTime: "5 min read",
    category: "Tutorials",
    content: `
## What Pinning Does in OmniCanvas

Pinning a note in OmniCanvas promotes it to the top of your notes list, regardless of which view you are using. Whether you are in grid view, list view, table view, or kanban view, pinned notes always appear first. This means your most important, most frequently accessed notes are always one glance away.

Think of pinning as a "favorites" system with a key advantage: pinned notes do not live in a separate section or folder. They stay exactly where they are in your folder and tag structure. They just float to the top of whatever view you are looking at. New to OmniCanvas? Start with the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas).

## When to Pin a Note

Not every important note deserves a pin. If you pin too many notes, the feature loses its value because your pinned section becomes just as cluttered as the rest of your list. Here are the situations where pinning works best:

### Active Project Hubs

If you have a note that serves as the central hub for a current project, pin it. This might be a canvas with your project roadmap, a note with key links and contacts, or a daily standup template. You will access this note multiple times per day, and pinning saves you from searching for it each time.

### Frequently Referenced Information

Some notes contain information you look up repeatedly: a credentials reference sheet, a style guide, a process checklist, or a contact directory. Pin these so they are always at the top of your list.

### Temporary Focus Items

Sometimes you need to keep a specific note front-of-mind for a few days. Maybe you are preparing for a presentation, working through a complex problem, or waiting for a response on something. Pin the note for the duration of that focus period, then unpin it when the moment passes.

## What Not to Pin

**Archival notes.** Notes you want to keep but rarely access should not be pinned. They will clutter your pinned section and push your truly active notes further down.

**Entire categories.** If you find yourself wanting to pin every note in a folder, that is a sign you should be using folder navigation instead. Pinning is for individual standout notes, not bulk categorization.

**Completed items.** Once a project wraps up or a reference note becomes outdated, unpin it immediately. Stale pins are worse than no pins because they train your eyes to ignore the pinned section entirely.

## A Pinning Strategy That Scales

Here is a simple rule of thumb: keep your pinned notes between three and seven items. This range is small enough that you can scan the full list instantly, but large enough to cover your key daily needs.

### The Three-Pin Minimum

At minimum, consider pinning:

1. **Your [daily dashboard](/blog/tutorials/build-daily-dashboard-canvas) or inbox canvas.** The note you open first every morning.
2. **Your most active project.** The note you are spending the most time on this week.
3. **Your quick-reference note.** The note with information you look up multiple times per day.

### Expanding to Five or Seven Pins

If three is not enough, add pins for:

4. A secondary active project.
5. A meeting notes template or running meeting log.
6. A personal goals or habit tracking note.
7. A temporary focus item that needs attention this week.

### The Weekly Unpin Review

Every Friday (or whatever day ends your work week), review your pinned notes. Ask two questions about each one:

- Did I access this note multiple times this week?
- Will I need quick access to it next week?

If the answer to both questions is no, unpin it. This five-minute review keeps your pinned section fresh and relevant.

## Combining Pins with Other Organization Features

Pinning works best as part of a broader organization system, not as a replacement for one.

### Pins Plus Folders

Use folders to organize notes by project or topic. Use pins to surface the one or two notes from each area that need daily attention. When you navigate into a specific folder, you see everything in that area. When you look at your full notes list, pinned items tell you where to focus.

### Pins Plus Tags

Tag your pinned notes with a tag like "active" or "current-focus." This way, even if you accidentally unpin something, you can quickly find it again by searching for the tag. It also gives you a way to review all currently active items in one search.

### Pins Plus Search

The Cmd+K search in OmniCanvas searches across all notes, including pinned ones. If your pinned list grows beyond seven items and you cannot decide what to unpin, use search as your overflow mechanism. Pin only the top items and rely on search for everything else.

## Pinning in Different Views

Pinned notes behave consistently across all OmniCanvas views:

- **Grid view:** Pinned notes appear as the first cards in the grid.
- **List view:** Pinned notes sit at the top of the list.
- **Table view:** Pinned notes occupy the first rows, and you can still sort and filter the remaining notes below them.
- **Kanban view:** Pinned notes are highlighted within their respective columns.

This consistency means you can switch views freely without losing track of your pinned items. The notes you care about most are always where you expect them to be.
`,
  },
  {
    slug: "bulk-actions-manage-notes-fast",
    title: "Bulk Actions: How to Manage Dozens of Notes in Seconds",
    description:
      "Use multi-select and bulk actions to move, delete or reorganize many notes at once. Workflows for spring cleaning, archiving and folder reorganization.",
    date: "2027-05-09",
    readTime: "5 min read",
    category: "Tutorials",
    content: `
## Why Bulk Actions Matter

As your OmniCanvas workspace grows from dozens of notes to hundreds, managing them one by one becomes painfully slow. Moving twelve notes to an archive folder individually means twelve open-edit-save cycles. Deleting thirty outdated notes means thirty trips to the delete button. Bulk actions let you select multiple notes at once and perform operations on all of them in a single step.

This guide covers the multi-select system in OmniCanvas and gives you practical workflows for common reorganization tasks. New to OmniCanvas? Start with the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas).

## How Multi-Select Works

To select multiple notes in OmniCanvas, hold down the selection modifier and click each note you want to include. Selected notes are visually highlighted so you can see exactly which ones are in your selection. You can select notes across different parts of your list, and the selection persists as you scroll.

Once you have selected your notes, a bulk action toolbar appears. This toolbar gives you the available operations: move to folder, delete, and other organizational actions. Choose an action and it applies to every selected note simultaneously.

### Selecting Efficiently

- **Click individual notes** to build a precise selection when you need specific notes from different parts of your list.
- **Use your current view to your advantage.** Switch to table view and sort by date, tag, or folder before selecting. This groups related notes together, making it easier to select a contiguous block.
- **Deselect mistakes** by clicking a selected note again to remove it from the selection.

## Workflow 1: Spring Cleaning Your Notes

Over time, every notetaking system accumulates clutter. Notes that made sense three months ago are now irrelevant. Draft canvases that never went anywhere are taking up visual space. Here is how to do a thorough cleanup efficiently:

### Step 1: Switch to Table View and Sort by Date

Table view shows your notes in a scannable, sortable format. Sort by date (oldest first) to surface notes you have not touched in months. These are your prime candidates for cleanup.

### Step 2: Scan and Select

Scroll through the oldest notes. Select every note that is clearly outdated, completed, or no longer relevant. Do not agonize over borderline cases yet. Focus on the obvious ones first.

### Step 3: Bulk Delete

With your selection made, use the bulk delete action. All selected notes move to the trash. Remember that OmniCanvas trash supports restore, so nothing is permanently lost. If you realize you deleted something you needed, you can recover it from the trash.

### Step 4: Handle Borderline Cases

Go back through the remaining old notes. For each borderline case, decide: move it to an "Archive" folder (to keep but deprioritize) or delete it. Use bulk move for the archive candidates.

## Workflow 2: Archiving a Completed Project

When a project wraps up, you want to keep the notes for reference but get them out of your active workspace. Bulk actions make this a two-minute task instead of a twenty-minute chore.

1. **Create an archive folder** if you do not have one. A top-level "Archive" folder with subfolders for each year or quarter works well.
2. **Search or filter for the project.** Use Cmd+K to search for the project name, or navigate to the project folder.
3. **Select all project notes.** Multi-select every note associated with the completed project.
4. **Bulk move to the archive folder.** All notes are relocated in one action.
5. **Unpin any pinned notes** from the project. Pinned notes for a completed project just add clutter to your daily view.

This workflow preserves your project history while keeping your active workspace clean and focused.

## Workflow 3: Reorganizing Your Folder Structure

Sometimes your folder structure needs to evolve. Maybe you started with broad folders and now need more specific ones. Maybe two projects merged and their notes should be consolidated. Bulk actions make folder reorganization practical.

### Splitting a Folder

If a folder has grown too large and covers too many topics:

1. Create the new, more specific subfolders.
2. Navigate to the oversized folder.
3. Switch to table view for easy scanning.
4. Select all notes that belong in the first new subfolder and bulk move them.
5. Repeat for each new subfolder.
6. Review whatever remains in the original folder and decide if it stays or moves.

### Merging Folders

If two folders overlap and you want to consolidate:

1. Open the folder you want to merge from.
2. Select all notes in that folder.
3. Bulk move them to the destination folder.
4. Delete the now-empty source folder.

### Retagging in Bulk

After a reorganization, you might want to update tags to match your new structure. While bulk tag editing applies to the selected notes, plan your new tagging scheme before you start selecting. A clear plan prevents you from having to redo the work.

## Tips for Effective Bulk Management

**Work in table view.** [Table view](/blog/tutorials/omnicanvas-table-view-power-tips) gives you the most information density and the easiest scanning experience for bulk operations. You can see titles, dates, folders, and tags all at once, and inline sorting helps you group related notes before selecting.

**Do bulk work on a schedule.** Set a monthly or quarterly reminder to review and reorganize. Small, regular cleanup sessions are far less overwhelming than a massive annual purge.

**Trust the trash.** OmniCanvas trash lets you restore deleted notes, so do not hesitate to delete aggressively during cleanup. If you are unsure about a note, delete it. If you need it later, restore it. This mindset keeps your workspace lean without risking permanent loss.

**Combine with search.** Before doing bulk operations, use Cmd+K to search for specific keywords, tags, or phrases. This can surface notes you forgot about and help you make better decisions about what to keep, move, or delete.
`,
  },
];
